import { ArrowDown, ArrowUpRight, Mail, MessageCircle } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "@/components/landing/Reveal";
import { getEmailLink, getWhatsAppLink, SITE } from "@/config/site";
import { usePageMeta } from "@/hooks/use-page-meta";

const QUESTIONS = [
  ["O que vale contar no primeiro contato?", "Explique o que precisa melhorar, como o processo funciona hoje e quem usa ou será impactado pela solução."],
  ["Preciso ter tudo definido?", "Não. A conversa inicial também serve para organizar o problema, entender prioridades e avaliar o melhor caminho."],
  ["Vocês trabalham com soluções já prontas?", "Sim. Podemos criar do zero ou adaptar uma solução existente quando isso fizer mais sentido para o projeto."],
];

const STARTING_POINTS = [
  ["O cenário", "O que acontece hoje e onde a operação perde tempo, informação ou oportunidade."],
  ["As pessoas", "Quem participa do processo, quem decide e quem precisa usar a solução no dia a dia."],
  ["A mudança", "O que precisa estar diferente para o projeto gerar valor de verdade para a empresa."],
];

export const ContactPage = () => {
  usePageMeta("Contato", "Converse com o Clube Digital sobre sistemas, CRM, sites, automações e soluções com inteligência artificial.");
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.8], [1, reduce ? 1 : 0.18]);

  return (
    <main>
      <section ref={heroRef} data-header-theme="dark" className="editorial-grid relative flex min-h-[100svh] items-end overflow-hidden bg-[#050606] pb-14 pt-36 text-white md:pb-20 md:pt-44">
        <div className="site-shell relative">
          <div className="flex items-center justify-between border-b border-white/12 pb-6">
            <p className="eyebrow">Contato</p>
            <p className="hidden text-[10px] font-bold uppercase tracking-[.18em] text-white/32 sm:block">Sem briefing perfeito</p>
          </div>
          <motion.h1 style={{ y: titleY, opacity: titleOpacity }} className="display-title mt-12 max-w-[1320px] text-[clamp(3.7rem,9vw,9.2rem)] leading-[.88]">Toda boa solução começa com uma boa conversa.</motion.h1>
          <div className="mt-14 flex flex-col gap-8 border-t border-white/12 pt-7 md:flex-row md:items-end md:justify-between">
            <p className="max-w-2xl text-base leading-relaxed text-white/58 md:text-lg">Conte o que sua empresa precisa organizar, automatizar ou construir. Nós ajudamos a transformar essa necessidade em um próximo passo claro.</p>
            <a href="#canais" className="group inline-flex w-fit items-center gap-3 text-xs font-bold uppercase tracking-[.16em] text-lime-300">Escolher um canal <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" /></a>
          </div>
        </div>
      </section>

      <section id="canais" data-header-theme="light" className="bg-[#f1f3ec] py-24 text-[#050606] md:py-32">
        <div className="site-shell">
          <div className="grid gap-8 border-b border-black/15 pb-10 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
            <p className="eyebrow !text-black/55">Escolha por onde começar</p>
            <h2 className="display-title text-[clamp(3rem,6.7vw,7rem)] leading-[.92]">Direto, simples e humano.</h2>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-2">
            <a href={getWhatsAppLink()} target="_blank" rel="noreferrer" className="contact-link group flex min-h-[430px] flex-col justify-between bg-lime-300 p-7 text-[#050606] transition-colors hover:bg-[#e8ee43] sm:p-10" data-testid="contact-whatsapp">
              <div className="flex items-start justify-between">
                <span className="grid h-14 w-14 place-items-center bg-[#050606] text-white"><MessageCircle size={22} /></span>
                <ArrowUpRight className="contact-arrow transition-transform" size={34} strokeWidth={1.4} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[.18em] text-black/48">Conversa direta</p>
                <h3 className="display-title mt-4 text-[clamp(2.7rem,5vw,5.5rem)] leading-[.94]">Falar no WhatsApp</h3>
                <div className="mt-7 flex items-center justify-between border-t border-black/20 pt-5 text-sm font-semibold"><span>{SITE.phoneDisplay}</span><span className="text-black/45">Abrir conversa</span></div>
              </div>
            </a>

            <a href={getEmailLink()} className="contact-link group flex min-h-[430px] flex-col justify-between bg-[#082d32] p-7 text-white transition-colors hover:bg-[#0b373d] sm:p-10" data-testid="contact-email">
              <div className="flex items-start justify-between">
                <span className="grid h-14 w-14 place-items-center bg-cyan-300 text-[#050606]"><Mail size={22} /></span>
                <ArrowUpRight className="contact-arrow transition-transform" size={34} strokeWidth={1.4} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[.18em] text-white/42">Mais contexto</p>
                <h3 className="display-title mt-4 text-[clamp(2.7rem,5vw,5.5rem)] leading-[.94]">Escrever por email</h3>
                <div className="mt-7 flex flex-col gap-2 border-t border-white/15 pt-5 text-sm font-semibold sm:flex-row sm:items-center sm:justify-between"><span>{SITE.email}</span><span className="text-white/38">Abrir email</span></div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section data-header-theme="light" className="bg-[#dfe9ff] py-24 text-[#0c1530] md:py-36">
        <div className="site-shell grid gap-14 lg:grid-cols-[.72fr_1.28fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow !text-[#0c1530]/55">Para a primeira conversa</p>
            <h2 className="display-title mt-7 text-[clamp(3rem,5.8vw,6.3rem)] leading-[.92]">Você não precisa chegar com a resposta.</h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-[#0c1530]/58">Alguns pontos ajudam a conversa a avançar, mas também podemos organizá-los juntos.</p>
          </Reveal>
          <div className="border-t border-[#0c1530]/15">
            {STARTING_POINTS.map(([title, text], index) => (
              <Reveal key={title} delay={index * .06}>
                <article className="grid gap-5 border-b border-[#0c1530]/15 py-9 sm:grid-cols-[54px_.65fr_1.35fr] sm:items-start">
                  <span className="text-[10px] font-bold text-[#2851aa]">0{index + 1}</span>
                  <h3 className="display-title text-2xl">{title}</h3>
                  <p className="max-w-xl text-sm leading-relaxed text-[#0c1530]/56 md:text-base">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-header-theme="dark" className="bg-[#050606] py-24 text-white md:py-36">
        <div className="site-shell grid gap-14 lg:grid-cols-[.62fr_1.38fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow">Antes de começar</p>
            <h2 className="display-title mt-7 text-[clamp(2.8rem,5vw,5.5rem)] leading-[.94]">Dúvidas que não precisam travar o primeiro contato.</h2>
          </Reveal>
          <div className="border-t border-white/12">
            {QUESTIONS.map(([question, answer], index) => (
              <Reveal key={question} delay={index * .05}>
                <article className="grid gap-5 border-b border-white/12 py-9 md:grid-cols-[46px_.85fr_1.15fr]">
                  <span className="text-[10px] text-white/25">0{index + 1}</span>
                  <h3 className="display-title text-xl md:text-2xl">{question}</h3>
                  <p className="text-sm leading-relaxed text-white/48 md:text-base">{answer}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};
